import { api } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BillingPlan {
  name: string;
  amount: number;
  currency: string;
  interval: string;
}

export interface PaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface BillingInfo {
  plan: BillingPlan | null;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  paymentMethod: PaymentMethod | null;
}

export interface InvoiceItem {
  id: string;
  number: string | null;
  date: string | null;
  amount: number;
  currency: string;
  status: string | null;
  pdfUrl: string | null;
}

export interface InvoicesResponse {
  invoices: InvoiceItem[];
  hasMore: boolean;
}

export interface PriceInfo {
  amount: number;
  currency: string;
  interval: string;
}

export interface PlanPrices {
  monthly: PriceInfo;
  yearly: PriceInfo;
}

export interface UpgradePreview {
  currentPlan: {
    name: string;
    amount: number;
    interval: string;
  };
  newPlan: {
    name: string;
    amount: number;
    interval: string;
  };
  proratedCredit: number;
  amountDue: number;
  currency: string;
  nextRenewalDate: string;
  annualSavings: number;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export function getPlanPrices() {
  return api.get<PlanPrices>("/stripe/prices");
}

export function createCheckoutSession(data: { plan: "monthly" | "yearly" }) {
  return api.post<{ url: string }>("/stripe/create-checkout-session", data);
}

export function createPortalSession() {
  return api.post<{ url: string }>("/stripe/create-portal-session");
}

export function getBillingInfo() {
  return api.get<BillingInfo>("/stripe/billing");
}

export function getInvoices(params?: {
  limit?: number;
  starting_after?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.limit) searchParams.set("limit", String(params.limit));
  if (params?.starting_after)
    searchParams.set("starting_after", params.starting_after);
  const query = searchParams.toString();
  return api.get<InvoicesResponse>(
    `/stripe/invoices${query ? `?${query}` : ""}`
  );
}

export function createPaymentUpdateSession() {
  return api.post<{ url: string }>("/stripe/create-payment-update-session");
}

export function getUpgradePreview() {
  return api.get<UpgradePreview>("/stripe/upgrade-preview");
}

export function switchToAnnual() {
  return api.post<{ success: boolean }>("/stripe/switch-to-annual");
}
